import { TooltipDirective } from '../../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../../componentes/botones';
import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnInit,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IFilaNS } from 'modelos/src';
import { ContentCardComponent } from '../../../../../../componentes/content-card/content-card.component';
import { ChipDatoComponent } from '../../filas-ns/chip-dato.component';
import { DialogService } from '../../../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { DispositivosLorawanService } from '../../dispositivos-lorawan.service';
import { fechaCelda } from '../../../../../../auxiliares/tabla/celdas';

// FILA DE DOWNLINKS DEL NETWORK SERVER (solapa exclusiva de cliente nivel 0).
//
// Cuando un downlink sale de nuestra fila todavía no está en el aire: entra a la
// fila del NS y espera a que el device abra una ventana de recepción. Ese tramo
// no lo administra el sistema, y hasta acá no se podía ni mirar.
//
// Importa poder mirarlo porque la fila no siempre se vacía: si el device es
// clase B/C y no tiene ruta de downlink (nadie lo escuchó últimamente), el
// scheduler de Chirpstack falla al elegirle gateway ANTES de mirarle el
// `expiresAt`. El item no se entrega, no vence, no se borra, y hace fallar al
// scheduler en cada tick. El botón de vaciar es la válvula de escape manual
// para cortar eso sin esperar al barrido automático.
@Component({
  selector: 'app-fila-ns',
  standalone: true,
  imports: [TooltipDirective, 
    CommonModule,
    BOTONES,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    ContentCardComponent,
    ChipDatoComponent,
  ],
  templateUrl: './fila-ns.component.html',
  styleUrl: './fila-ns.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FilaNSComponent implements OnInit {
  // _id del dispositivo LoRaWAN (no el deveui): el endpoint resuelve el deveui
  // y valida el permiso sobre la entidad.
  @Input({ required: true }) public idDispositivo!: string;

  // Con tarjeta (solapa del detalle) o pelado (diálogo). En el diálogo el
  // título y el botón de cerrar ya los pone el header del diálogo: meter otra
  // tarjeta adentro encima bordes, sombras y esquinas redondeadas, y repite el
  // título dos veces.
  @Input() public enTarjeta = true;

  private service = inject(DispositivosLorawanService);
  private helper = inject(HelperService);
  private dialog = inject(DialogService);

  public fila?: IFilaNS;
  public cargando = false;
  public flusheando = false;

  get hayItems(): boolean {
    return !!this.fila?.totalItems;
  }

  // Un item vencido que sigue encolado es basura por definición nuestra: todo
  // downlink sale con `expiresAt` (15 min reconciliación, 5 min manual).
  // Descartable = vencido O sin vencimiento. El segundo caso es el más grave:
  // Chirpstack no lo va a soltar nunca por su cuenta. Sólo un item con
  // vencimiento en el FUTURO justifica no tocar la fila.
  get todosDescartables(): boolean {
    if (!this.fila || !this.fila.totalItems) return false;
    return (
      this.fila.itemsVencidos + this.fila.itemsSinVencimiento ===
      this.fila.totalItems
    );
  }

  get itemsVigentes(): number {
    if (!this.fila) return 0;
    return (
      this.fila.totalItems -
      this.fila.itemsVencidos -
      this.fila.itemsSinVencimiento
    );
  }

  // El bucle del scheduler sólo existe para clase B/C y sin ruta de downlink.
  // Las dos condiciones juntas son el diagnóstico completo.
  get esFilaHuerfana(): boolean {
    return !!this.fila?.claseBC && !!this.fila?.offline && this.hayItems;
  }

  async ngOnInit(): Promise<void> {
    await this.cargar();
  }

  public async cargar(): Promise<void> {
    if (this.cargando) return;
    this.cargando = true;
    try {
      this.fila = await this.service.getFilaNS(this.idDispositivo);
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.cargando = false;
    }
  }

  public async flush(): Promise<void> {
    if (this.flusheando || !this.fila) return;

    const total = this.fila.totalItems;
    const vigentes = this.itemsVigentes;
    const advertencia = vigentes
      ? `\n\nOJO: ${vigentes} de esos ${total} siguen VIGENTES. Si el ` +
        `dispositivo volviera a comunicar antes de su vencimiento, el network ` +
        `server se los entregaría.`
      : '';

    const ok = await this.dialog.confirm(
      'Vaciar la fila del network server',
      `Se van a borrar los ${total} downlinks que Chirpstack tiene pendientes ` +
        `para ${this.fila.deveui}. Chirpstack no permite borrar de a uno: se ` +
        `borra la fila completa.${advertencia}\n\n` +
        `Lo que era configuración lo vuelve a generar el reconciliador en su ` +
        `próximo ciclo. Lo que era un comando manual hay que volver a mandarlo.`,
      'Vaciar la fila',
      'Cancelar',
    );
    if (!ok) return;

    this.flusheando = true;
    try {
      const r = await this.service.flushFilaNS(this.idDispositivo);
      if (r?.ok) {
        this.helper.notifSuccess(
          `Fila vaciada: ${r.itemsBorrados} downlink(s) borrados`,
        );
      } else {
        this.helper.notifWarn('Chirpstack no confirmó el vaciado de la fila');
      }
      await this.cargar();
    } catch (error) {
      this.helper.notifError(error);
    } finally {
      this.flusheando = false;
    }
  }

  /** El template ya muestra «Sin vencimiento» cuando falta la fecha. */
  public formatearFecha(fecha?: string): string {
    return fechaCelda(fecha);
  }
}
